/**
 * A passagem de plantão (`TP-069`): a Case de gerenciamento, no call, vê as divergências entre o pedido e o IW que a
 * plataforma achou, confirma, apaga ou acrescenta, e registra que recebeu. O Caso passa a ser dela. A divergência não
 * bloqueia nada: vira pedido de ajuste para a captação, que marca quando a guia foi corrigida. A coordenação valida.
 */

export type Conferencia = 'sem_divergencia' | 'com_divergencia';

/** O que se compara no pedido × IW (`D3`); `outro` cobre o que a pessoa achar fora deles. */
export type GrupoDoItem = 'paciente' | 'admissao' | 'terapias' | 'outro';

export const GRUPOS_DO_ITEM: readonly GrupoDoItem[] = ['paciente', 'admissao', 'terapias', 'outro'];

export const ROTULOS_DO_GRUPO: Record<GrupoDoItem, string> = {
  paciente: 'Paciente',
  admissao: 'Admissão',
  terapias: 'Terapias',
  outro: 'Outro',
};

export function ehGrupoDoItem(valor: string | null | undefined): valor is GrupoDoItem {
  return GRUPOS_DO_ITEM.includes(valor as GrupoDoItem);
}

/** Por que a conferência automática não rodou (`D4`). A passagem segue mesmo assim. */
export type MotivoSemConferencia = 'sem_vinculo_iw' | 'pedido_sem_chave' | 'iw_indisponivel';

export const TEXTO_SEM_CONFERENCIA: Record<MotivoSemConferencia, string> = {
  sem_vinculo_iw: 'O paciente não está vinculado ao IW, então a plataforma não conferiu o pedido com o IW.',
  pedido_sem_chave: 'O pedido não tem carteira, CPF ou nome confiáveis para achar o paciente no IW.',
  iw_indisponivel: 'Não foi possível ler o IW agora.',
};

export function ehMotivoSemConferencia(valor: string | null | undefined): valor is MotivoSemConferencia {
  return valor === 'sem_vinculo_iw' || valor === 'pedido_sem_chave' || valor === 'iw_indisponivel';
}

/** O limite de cada texto, o mesmo do CHECK da `0060`. */
export const TAMANHO_MAXIMO_DO_TEXTO = 200;

export interface DivergenciaDaPassagem {
  readonly id: string;
  readonly grupo: GrupoDoItem;
  readonly campo: string;
  readonly noPedido: string | null;
  readonly noIw: string | null;
  /** Veio da conferência automática e a pessoa confirmou. */
  readonly sugerida: boolean;
  readonly aberta: boolean;
  readonly corrigidaEm: Date | null;
  readonly corrigidaPorNome: string | null;
}

export interface PassagemDePlantao {
  readonly id: string;
  /** `null`: o Caso não tinha dono, ou o id não está no cadastro. */
  readonly deNome: string | null;
  readonly paraNome: string | null;
  readonly registradaEm: Date;
  readonly conferencia: Conferencia;
  readonly validadaEm: Date | null;
  readonly validadaPorNome: string | null;
  readonly divergencias: readonly DivergenciaDaPassagem[];
}

/** Uma linha do formulário: o que a conferência sugeriu (para a pessoa conferir) ou o que a pessoa escreveu. */
export interface LinhaDeDivergencia {
  readonly grupo: GrupoDoItem;
  readonly campo: string;
  readonly noPedido: string;
  readonly noIw: string;
  readonly sugerida: boolean;
}

/** Um documento do Processo Inicial (`D11`): só mostra, não trava. */
export interface ItemDoProcessoInicial {
  readonly chave: string;
  readonly rotulo: string;
  readonly presente: boolean;
}

export interface PassagemDoCaso {
  /** Caso autorizado e sem passagem. */
  readonly podeRegistrar: boolean;
  /** Quem olha já é dona do Caso: não há passagem para registrar (`D9`). */
  readonly casoEhSeu: boolean;
  /** Quem olha passou ou recebeu: não valida (`D8`). */
  readonly souParte: boolean;
  readonly passagem: PassagemDePlantao | null;
  /** A versão da conferência do pedido × IW que rodou; `null` quando ela não rodou. */
  readonly conferenciaAutomatica: string | null;
  readonly semConferencia: MotivoSemConferencia | null;
  readonly sugestoes: readonly LinhaDeDivergencia[];
  /** `null`: a completude não pôde ser lida agora, ou o Caso não está autorizado. */
  readonly processoInicial: readonly ItemDoProcessoInicial[] | null;
}

export const LINHA_VAZIA: LinhaDeDivergencia = { grupo: 'outro', campo: '', noPedido: '', noIw: '', sugerida: false };

/** A linha que o servidor recusaria: campo em branco ou algum texto acima do limite. */
export function linhaInvalida(linha: LinhaDeDivergencia): boolean {
  return (
    linha.campo.trim() === '' ||
    linha.campo.trim().length > TAMANHO_MAXIMO_DO_TEXTO ||
    linha.noPedido.trim().length > TAMANHO_MAXIMO_DO_TEXTO ||
    linha.noIw.trim().length > TAMANHO_MAXIMO_DO_TEXTO
  );
}
